import { useLocation, useNavigate } from "react-router-dom"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"

interface Tab {
  label: string
  path: string
}

/**
 * Sub-navigation for the settings pages so a user can jump between Profile and
 * Company Settings without reopening the header dropdown. The Company tab only
 * appears for users who can manage users (owners/staff), matching both the
 * dropdown gating and the CompanySettingsPage route guard. Underline-style tabs
 * sit on a shared bottom border; the active tab is drawn in the brand color.
 */
export function SettingsTabs() {
  const { theme } = useClientTheme()
  const { canManageUsers } = useCurrentUser()
  const location = useLocation()
  const navigate = useNavigate()

  const tabs: Tab[] = [
    { label: "Profile", path: "/settings/profile" },
    ...(canManageUsers ? [{ label: "Company", path: "/settings/company" }] : []),
  ]

  return (
    <div
      className="flex items-center"
      style={{ gap: "4px", borderBottom: `1px solid ${theme.borderColor}`, marginBottom: "20px" }}
    >
      {tabs.map((tab) => {
        const active = location.pathname === tab.path
        return (
          <button
            key={tab.path}
            type="button"
            onClick={() => !active && navigate(tab.path)}
            aria-current={active ? "page" : undefined}
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "14px",
              fontWeight: active ? 600 : 500,
              color: active ? theme.brandPrimary : theme.textMuted,
              background: "none",
              border: "none",
              padding: "8px 12px",
              cursor: active ? "default" : "pointer",
              // Sit the active underline on top of the container's border.
              borderBottom: `2px solid ${active ? theme.brandPrimary : "transparent"}`,
              marginBottom: "-1px",
              transition: "color 120ms ease",
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.color = theme.textPrimary
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.color = theme.textMuted
            }}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
